import { useCallback, useEffect, useMemo, useState } from 'react';

const storagePrefix = 'mgr:page-size';
const anonymousUser = 'anonymous';

const getStorageKey = (tableId, userId) => (
  tableId ? `${storagePrefix}:${userId || anonymousUser}:${tableId}` : null
);

function normalizeOptions(options) {
  return [...new Set((options || []).map(Number).filter(value => Number.isInteger(value) && value > 0))];
}

function normalizePageSize(value, options, defaultPageSize) {
  const parsed = Number(value);
  return options.includes(parsed) ? parsed : defaultPageSize;
}

function readPageSize(storageKey, options, defaultPageSize) {
  if (!storageKey || typeof window === 'undefined') {
    return defaultPageSize;
  }

  try {
    return normalizePageSize(window.localStorage.getItem(storageKey), options, defaultPageSize);
  } catch (err) {
    console.warn('Failed to read page size:', err);
    return defaultPageSize;
  }
}

function writePageSize(storageKey, pageSize, defaultPageSize) {
  if (!storageKey || typeof window === 'undefined') {
    return;
  }

  try {
    if (pageSize === defaultPageSize) {
      window.localStorage.removeItem(storageKey);
    } else {
      window.localStorage.setItem(storageKey, String(pageSize));
    }
  } catch (err) {
    console.warn('Failed to save page size:', err);
  }
}

export function usePageSizePreference({ tableId, userId, options, defaultPageSize }) {
  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const normalizedDefault = normalizePageSize(defaultPageSize, normalizedOptions, normalizedOptions[0]);
  const storageKey = useMemo(() => getStorageKey(tableId, userId), [tableId, userId]);
  const [pageSize, setPageSize] = useState(() => (
    readPageSize(storageKey, normalizedOptions, normalizedDefault)
  ));

  useEffect(() => {
    setPageSize(readPageSize(storageKey, normalizedOptions, normalizedDefault));
  }, [normalizedDefault, normalizedOptions, storageKey]);

  const onPageSizeChange = useCallback((value) => {
    const nextPageSize = normalizePageSize(value, normalizedOptions, normalizedDefault);
    writePageSize(storageKey, nextPageSize, normalizedDefault);
    setPageSize(nextPageSize);
  }, [normalizedDefault, normalizedOptions, storageKey]);

  return { pageSize, onPageSizeChange };
}
